import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";

const Dashboard = () => {
  useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useSelector((state) => state.user);
  usePermissions();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else {
      // Redirect to my-business instead of staying on dashboard
      navigate("/my-business");
    }
  }, [isAuthenticated, navigate]);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        <div style={{
          ...styles.welcomeCard,
          padding: isMobile ? "20px" : "32px",
        }}>
          <h2 style={{
            ...styles.welcomeTitle,
            fontSize: isMobile ? "24px" : "28px",
          }}>Welcome, {user?.name || "User"}!</h2>
          <p style={styles.welcomeText}>
            You have successfully logged in to your account.
          </p>
        </div>

        <div style={{
          ...styles.cardsContainer,
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(300px, 1fr))",
          gap: isMobile ? "16px" : "20px",
        }}>
          <div style={{
            ...styles.card,
            padding: isMobile ? "20px" : "24px",
          }}>
            <h3 style={{
              ...styles.cardTitle,
              fontSize: isMobile ? "18px" : "20px",
            }}>User Information</h3>
            <div style={styles.cardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Name:</span>
                <span style={styles.infoValue}>{user?.name || "N/A"}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Email:</span>
                <span style={styles.infoValue}>{user?.email || "N/A"}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Role:</span>
                <span style={styles.infoValue}>{user?.role || "user"}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Account Created:</span>
                <span style={styles.infoValue}>
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div style={{
            ...styles.card,
            padding: isMobile ? "20px" : "24px",
          }}>
            <h3 style={{
              ...styles.cardTitle,
              fontSize: isMobile ? "18px" : "20px",
            }}>Quick Actions</h3>
            <div style={styles.cardContent}>
              <PermissionGate resource="profile" action="update">
                <Link to="/profile" style={styles.actionButton}>
                  Update Profile
                </Link>
              </PermissionGate>
              <PermissionGate resource="profile" action="update">
                <Link to="/change-password" style={styles.actionButton}>
                  Change Password
                </Link>
              </PermissionGate>
              <PermissionGate resource="roles" action="read">
                <Link to="/roles" style={styles.actionButton}>
                  Manage Roles
                </Link>
              </PermissionGate>
              <PermissionGate resource="users" action="read">
                <Link to="/users" style={styles.actionButton}>
                  Manage Users
                </Link>
              </PermissionGate>
              <PermissionGate resource="contacts" action="read">
                <Link to="/contacts" style={styles.actionButton}>
                  Manage Contacts
                </Link>
              </PermissionGate>
              <PermissionGate resource="products" action="read">
                <Link to="/products" style={styles.actionButton}>
                  Manage Products
                </Link>
              </PermissionGate>
              <PermissionGate resource="parties" action="read">
                <Link to="/parties" style={styles.actionButton}>
                  Manage Parties
                </Link>
              </PermissionGate>
              <PermissionGate resource="profile" action="read">
                <Link to="/my-business" style={styles.actionButton}>
                  My Business
                </Link>
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>
      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "24px",
    minHeight: "calc(100vh - 80px)",
  },
  welcomeCard: {
    backgroundColor: "#ffeef2",
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "30px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    border: "1px solid #ffd6e0",
  },
  welcomeTitle: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#111b21",
    marginBottom: "12px",
    letterSpacing: "-0.02em",
  },
  welcomeText: {
    fontSize: "16px",
    color: "#667781",
    fontWeight: "400",
    letterSpacing: "-0.01em",
  },
  cardsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  card: {
    backgroundColor: "#ffeef2",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #ffd6e0",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    color: "#111b21",
    marginBottom: "20px",
    letterSpacing: "-0.01em",
    paddingBottom: "16px",
    borderBottom: "1px solid #ffd6e0",
  },
  cardContent: {
    fontSize: "15px",
    color: "#111b21",
    lineHeight: "1.8",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    padding: "8px 0",
  },
  infoLabel: {
    fontWeight: "500",
    color: "#667781",
    flexShrink: 0,
  },
  infoValue: {
    fontWeight: "400",
    color: "#111b21",
    textAlign: "right",
    flex: 1,
  },
  actionButton: {
    display: "block",
    padding: "12px 16px",
    marginBottom: "10px",
    backgroundColor: "#00a884",
    color: "#ffffff",
    textDecoration: "none",
    borderRadius: "24px",
    textAlign: "center",
    fontWeight: "400",
    transition: "background-color 0.2s, transform 0.2s",
    fontSize: "14px",
    letterSpacing: "-0.01em",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
};

export default Dashboard;

